import React, { useState, useEffect, useRef } from 'react';
import { submitActivityDualWrite } from '../services/activityDualWriteService';
import { CredentialsService } from '../services/credentialsService';
import { Icon } from './Icon';
import { useDialogA11y } from '../hooks/useDialogA11y';
import './ActivityLogger.css';

interface WellnessDimension {
  id: string;
  name: string;
  description?: string;
  icon: string;
  color: string;
}

interface ActivityLoggerProps {
  dimensions: WellnessDimension[];
  facilityId: string;
  residentId: string; // From JWT payload
  externalId: string; // From JWT payload
  isExpanded?: boolean;
  onToggle?: (nextExpanded: boolean) => void;
  onSubmit?: (activity: string, selectedDimensions: string[]) => void;
}

export const ActivityLogger: React.FC<ActivityLoggerProps> = ({
  dimensions,
  facilityId,
  residentId,
  externalId,
  isExpanded: isExpandedProp,
  onToggle,
  onSubmit
}) => {
  const [activity, setActivity] = useState('');
  const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const [activityEnvOverride, setActivityEnvOverride] = useState<'sandbox' | 'production' | null>(null);
  const isExpanded = typeof isExpandedProp === 'boolean' ? isExpandedProp : internalIsExpanded;
  const setExpanded = (nextExpanded: boolean) => {
    onToggle?.(nextExpanded);
    if (typeof isExpandedProp !== 'boolean') {
      setInternalIsExpanded(nextExpanded);
    }
  };

  useEffect(() => {
    if (!facilityId) {
      setActivityEnvOverride(null);
      return;
    }
    let isActive = true;
    CredentialsService.getCredentialStatus(facilityId)
      .then((credentials) => {
        if (!isActive) return;
        if (credentials) {
          setActivityEnvOverride(credentials.isSandbox ? 'sandbox' : 'production');
        } else {
          setActivityEnvOverride(null);
        }
      })
      .catch((error) => {
        console.warn('⚠️ Unable to resolve CareDaily env for activity logging.', error);
        if (isActive) {
          setActivityEnvOverride(null);
        }
      });
    return () => {
      isActive = false;
    };
  }, [facilityId]);

  const successModalRef = useRef<HTMLDivElement>(null);
  useDialogA11y({
    isOpen: showSuccess,
    onClose: () => setShowSuccess(false),
    dialogRef: successModalRef,
  });

  const errorModalRef = useRef<HTMLDivElement>(null);
  useDialogA11y({
    isOpen: !!errorMessage,
    onClose: () => setErrorMessage(null),
    dialogRef: errorModalRef,
  });

  const handleDimensionToggle = (dimensionId: string) => {
    const newSelected = new Set(selectedDimensions);
    if (newSelected.has(dimensionId)) {
      newSelected.delete(dimensionId);
    } else {
      newSelected.add(dimensionId);
    }
    setSelectedDimensions(newSelected);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activity.trim() || selectedDimensions.size === 0) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const selectedDimensionsResolved = dimensions.filter(d => selectedDimensions.has(d.id));
      const selectedDimensionNames = selectedDimensionsResolved.map(d => d.name);
      const selectedDimensionIds = selectedDimensionsResolved.map(d => d.id);

      const result = await submitActivityDualWrite({
        facilityId,
        residentId,
        externalId,
        activityText: activity.trim(),
        dimensionIds: selectedDimensionIds,
        dimensionNames: selectedDimensionNames,
      }, {
        envOverride: activityEnvOverride || undefined,
      });

      // If BOTH writes failed, treat as an error. Otherwise show success (safety mechanism).
      if (!result.local.ok && !result.caredaily.ok) {
        setErrorMessage(result.caredaily.error || result.local.error || 'Failed to log activity. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Call the callback if provided
      if (onSubmit) {
        onSubmit(activity, selectedDimensionNames);
      }

      // Show success feedback
      setShowSuccess(true);
      
      // Clear form
      setActivity('');
      setSelectedDimensions(new Set());

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Failed to log activity:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to log activity. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = activity.trim().length > 0 && selectedDimensions.size > 0;

  return (
    <div className="activity-logger">
      <div className="activity-logger-header">
        <div 
          className="activity-logger-title-row"
          onClick={() => setExpanded(!isExpanded)}
        >
          <div>
            <h3>Activity Tracking</h3>
            <p className="activity-logger-subtitle">
           Add any activity completed outside of your community calendar to count it towards your progress.
            </p>
          </div>
          <button
            type="button"
            className={`toggle-button ${isExpanded ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!isExpanded);
            }}
            aria-label={isExpanded ? 'Collapse activity form' : 'Expand activity form'}
          >
            {isExpanded ? (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            ) : (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
          </button>
        </div>
      </div>

      <div
        className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}
        aria-hidden={!isExpanded}
      >
        <div className="collapsible-inner">
          <form onSubmit={handleSubmit} className="activity-logger-form">
            {/* Activity Text Field */}
            <div className="form-group">
              <label htmlFor="activity-input" className="form-label">
                What did you do?
              </label>
              <textarea
                id="activity-input"
                className="activity-input"
                value={activity}
                onChange={(e) => setActivity(e.target.value)}
                placeholder="e.g., Went for a 30-minute walk in the garden"
                rows={3}
                disabled={isSubmitting}
              />
              <div className="input-hint">
                {activity.length}/500 characters
              </div>
            </div>

            {/* Wellness Dimensions Multi-Select */}
            <div className="form-group">
              <label className="form-label">
                Which wellness dimensions does this activity support?
                <span className="dimension-count">
                  {selectedDimensions.size > 0 && ` (${selectedDimensions.size} selected)`}
                </span>
              </label>
              <div className="dimensions-grid">
                {dimensions.map((dimension) => {
                  const isSelected = selectedDimensions.has(dimension.id);
                  return (
                    <button
                      key={dimension.id}
                      type="button"
                      className={`dimension-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleDimensionToggle(dimension.id)}
                      style={{
                        '--dimension-color': dimension.color
                      } as React.CSSProperties}
                      disabled={isSubmitting}
                    >
                      <span className="dimension-icon" style={{ color: isSelected ? 'white' : dimension.color }}>
                        <Icon name={dimension.icon} size={24} decorative />
                      </span>
                      <span className="dimension-text">
                        <span className="dimension-name">{dimension.name}</span>
                        {dimension.description && (
                          <span className="dimension-description">{dimension.description}</span>
                        )}
                      </span>
                      {isSelected && <span className="dimension-check"><Icon name="FaCheck" size={16} /></span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    <span>Logging...</span>
                  </>
                ) : (
                  <>
                    <span><Icon name="FaClipboardList" size={18} decorative /></span>
                    <span>Log Activity</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="activity-modal-overlay" onClick={() => setShowSuccess(false)}>
          <div
            ref={successModalRef}
            tabIndex={-1}
            className="activity-modal activity-modal--success"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-success-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="activity-modal__icon"><Icon name="FaCheck" size={64} decorative /></div>
            <h3 className="activity-modal__title" id="activity-success-modal-title">Success!</h3>
            <p className="activity-modal__message">Your activity has been logged successfully.</p>
            <button 
              className="activity-modal__button"
              onClick={() => setShowSuccess(false)}
            >
              Great!
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div className="activity-modal-overlay" onClick={() => setErrorMessage(null)}>
          <div
            ref={errorModalRef}
            tabIndex={-1}
            className="activity-modal activity-modal--error"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-error-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="activity-modal__icon"><Icon name="FaExclamationTriangle" size={64} /></div>
            <h3 className="activity-modal__title" id="activity-error-modal-title">Unable to Log Activity</h3>
            <p className="activity-modal__message">{errorMessage}</p>
            <button 
              className="activity-modal__button"
              onClick={() => setErrorMessage(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

